'use client';
import * as React from 'react';
import { useState } from 'react';
import type { GetServerSideProps } from 'next';

import { contentfulAPIURL } from '@/clients/contentful';
import { createApolloClient } from '@/clients/apollo';
import { queryFooter, queryNavbar } from '@/clients/queries';
import { FooterQuery, NavbarQuery } from 'types/generated';

import PageContainer from '@/components/PageContainer';
import { RadioSchedule } from '@/models/RadioTypes';
import { getRadioSchedules } from '@/clients/RadioUtils';

import { Log } from '@/logging/datadog';
import { FOXWAY_CRITERIA_TRANSLATIONS } from '@/config/constants';

type Props = {
  navbar: NavbarQuery;
  footer: FooterQuery;
  radioSchedules: Record<string, RadioSchedule[]>;
};

type FoxwayDevice = {
  Id: number;
  ItemName: string;
  ProductName: string;
  ProductNameShort: string;
  Price: number;
  CurrencyIsoCode: string;
  PictureUrl?: string;
  ManufacturerId: number;
};

type DeviceModel = {
  id: number;
  itemName: string;
  productName: string;
  price: number;
  currency: string;
  imageUrl?: string;
};

type FoxwayGradingForm = {
  Questions: Array<{
    Id: number;
    Name: string;
    Label: string;
    InfoCards?: Array<{
      Id: number;
      Label: string;
      Text: string;
      Images: string[];
    }>;
    Answers: Array<{
      Id: number;
      Name: string;
      Score: number;
    }>;
  }>;
};

type GradingQuestion = {
  id: number;
  question: string;
  label: string;
  infoCardText?: string;
  infoCardImages?: string[];
  options: Array<{
    id: number;
    name: string;
    score: number;
  }>;
};

type PriceQuote = {
  price: number;
  currency: string;
  modelId: number;
};

// Translation map for step labels
const STEP_LABEL_TRANSLATIONS: Record<string, string> = {
  Power: 'Virkni',
  LCD: 'Skjár',
  Display: 'Skjár',
  Body: 'Útlit',
  Boot: 'Ræsing',
  Enclosure: 'Umgjörð',
  Buttons: 'Takkar',
};

// Translation map for questions
const QUESTION_TRANSLATIONS: Record<string, string> = {
  'Does the unit power on?': 'Virkar tækið eðlilega?',
  'Does the device turn on and stay on without a charger?': 'Virkar tækið eðlilega?',
  'Is the screen ok?': 'Er skjárinn í lagi?',
  'Is LCD working and without physical damage?': 'Er skjárinn í lagi?',
  'Are there any cracks or spots on the screen? (Looking at from 30 cm away)': 'Er skjárinn í lagi?',
  'Does the operating system boot up?': 'Ræsir stýrikerfið?',
  'Is computer physically ok?': 'Er útlitið í lagi?',
  'Are front, frame and back cover in good condition (general wear is acceptable)?': 'Er útlitið í lagi?',
  'Any scratches or dents visible at 30 cm?': 'Er útlitið í lagi?',
  'Are all buttons present and working?': 'Virka allir takkar?',
  'Do all buttons work as intended?': 'Virka allir takkar?',
};

// Translation map for answers
const ANSWER_TRANSLATIONS: Record<string, string> = {
  Yes: 'Já, allt er í lagi',
  No: 'Nei, ekki alveg allt',
  'Yes, everything works': 'Já, allt er í lagi',
  'No, not everything works': 'Nei, ekki alveg allt',
};

// Function to translate InfoCard HTML text
const translateInfoCardText = (htmlText: string): string => {
  let translated = htmlText;

  // Sort by length descending to replace longer phrases first (avoid partial replacements)
  const sortedTranslations = Object.entries(FOXWAY_CRITERIA_TRANSLATIONS).sort((a, b) => b[0].length - a[0].length);

  sortedTranslations.forEach(([english, icelandic]) => {
    const escapedEnglish = english.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Match both within tags and also handle &quot; entities
    const regex1 = new RegExp(`>${escapedEnglish}<`, 'gi');
    const regex2 = new RegExp(escapedEnglish.replace(/"/g, '&quot;'), 'gi');
    const regex3 = new RegExp(escapedEnglish, 'gi'); // Match anywhere
    translated = translated.replace(regex1, `>${icelandic}<`);
    translated = translated.replace(regex2, icelandic);
    translated = translated.replace(regex3, icelandic);
  });
  return translated;
};

const ReiknivelPage = ({ navbar, footer, radioSchedules }: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [devices, setDevices] = useState<DeviceModel[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<DeviceModel | null>(null);
  const [gradingQuestions, setGradingQuestions] = useState<GradingQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [priceQuote, setPriceQuote] = useState<PriceQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'search' | 'grading' | 'result'>('search');
  const [_showDropdown, setShowDropdown] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showRequirements, setShowRequirements] = useState(false);
  const [isHoveringDropdown, setIsHoveringDropdown] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<number | null>(null);
  const [activeOption, setActiveOption] = useState<number | null>(null);

  // Debounced search function
  React.useEffect(() => {
    if (!searchTerm.trim()) {
      setDevices([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`/api/foxway/models?term=${encodeURIComponent(searchTerm)}`);

        if (!response.ok) {
          throw new Error('Ekki tókst að sækja tæki');
        }

        const data = await response.json();

        // Map Foxway API response to our DeviceModel type
        const mappedDevices: DeviceModel[] = (data.Data || []).map((device: FoxwayDevice) => ({
          id: device.Id,
          itemName: device.ItemName,
          productName: device.ProductNameShort || device.ProductName,
          price: device.Price,
          currency: device.CurrencyIsoCode,
          imageUrl: device.PictureUrl,
        }));

        setDevices(mappedDevices);
        setShowDropdown(mappedDevices.length > 0);
        setError(null);

        if (mappedDevices.length === 0) {
          setError('Engin tæki fundust');
        }
      } catch (err) {
        Log.error('Error searching devices:', err);
        setError('Villa kom upp við leit');
        setDevices([]);
        setShowDropdown(false);
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const selectDevice = async (device: DeviceModel) => {
    setSelectedDevice(device);
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/foxway/grading-form?modelId=${device.id}`);

      if (!response.ok) {
        throw new Error('Ekki tókst að sækja spurningar');
      }

      const data: FoxwayGradingForm = await response.json();

      console.log('=== GRADING FORM DATA ===', data);

      // Map Foxway grading form to our format
      const mappedQuestions: GradingQuestion[] = (data.Questions || []).map((q) => {
        const infoCard = q.InfoCards && q.InfoCards.length > 0 ? q.InfoCards[0] : null;
        return {
          id: q.Id,
          question: q.Name,
          label: q.Label,
          infoCardText: infoCard?.Text,
          infoCardImages: infoCard?.Images,
          options: q.Answers.map((a) => ({
            id: a.Id,
            name: a.Name,
            score: a.Score,
          })),
        };
      });

      setGradingQuestions(mappedQuestions);
      setCurrentQuestionIndex(0);
      setStep('grading');
    } catch (err) {
      Log.error('Error fetching grading form:', err);
      setError('Villa kom upp við að sækja spurningar');
    } finally {
      setLoading(false);
    }
  };

  const submitGrading = async (finalAnswers?: Record<number, string>) => {
    if (!selectedDevice) return;

    const answersToSubmit = finalAnswers || answers;

    // Validate all questions are answered (using the passed answers or current state)
    const unansweredQuestions = gradingQuestions.filter((q) => !answersToSubmit[q.id]);
    if (unansweredQuestions.length > 0) {
      setError('Vinsamlegast svaraðu öllum spurningum');
      return;
    }

    setLoading(true);
    setError(null);

    // Convert answers to Foxway's expected format
    // Per Foxway support: remove the 4th grading answer (index 3) and SystemExtras
    const gradingFormAnswers = Object.fromEntries(
      Object.entries(answersToSubmit)
        .filter(([questionId]) => questionId !== '3') // Remove 4th answer per Foxway
        .map(([questionId, answerId]) => [questionId, [parseInt(answerId)]])
    );

    const payload = {
      GradingForm: {
        Answers: gradingFormAnswers,
        Extras: [],
        GradingDifferenceImages: {},
      },
      ModelId: selectedDevice.id,
      ItemIdentifier: '',
      LocationId: null,
    };

    try {
      const response = await fetch('/api/foxway/price-quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ekki tókst að fá verðtilboð');
      }

      const data = await response.json();

      // Map Foxway response to our format
      const rawPrice = data.OfferedPrice || data.Price || data.price || 0;

      const mappedQuote = {
        price: rawPrice,
        currency: data.CurrencyIsoCode || data.Currency || data.currency || 'ISK',
        modelId: data.ModelId || data.modelId || selectedDevice.id,
      };

      setPriceQuote(mappedQuote);
      setStep('result');
    } catch (err) {
      Log.error('Error getting price quote:', err);
      setError('Villa kom upp við að fá verðtilboð');
    } finally {
      setLoading(false);
    }
  };

  const resetCalculator = () => {
    setSearchTerm('');
    setDevices([]);
    setSelectedDevice(null);
    setGradingQuestions([]);
    setAnswers({});
    setPriceQuote(null);
    setError(null);
    setStep('search');
    setCurrentQuestionIndex(0);
    setShowDropdown(false);
  };

  return (
    <>
      <PageContainer
        title="Sýn | Innkaupareiknivél"
        metaTags={{
          title: 'Innkaupareiknivél | Vefverslun Sýnar',
          url: 'https://www.syn.is/vefverslun/reiknivel',
          keywords: 'syn,innkaup,reiknivél,tæki,vefverslun,sýn',
          image: { url: '/android-chrome-192x192.png' },
        }}
        navbar={navbar}
        footer={footer}
        radioSchedules={radioSchedules}
      >
        <div className="col-span-full mt-[128px]">
          <div className="mx-8 mt-16 w-full max-w-6xl px-4 text-start md:mx-auto">
            <div className="prose prose-lg flex max-w-none flex-wrap items-center gap-8 md:flex-nowrap md:gap-16">
              <a className="capitalize text-primary-faded-text-100" href="/vefverslun">
                Vefverslun
              </a>
              <div className="text-red-100">•</div>
              <span className="text-primary-faded-text-100">Reiknivél</span>
            </div>
          </div>
        </div>

        <div className="col-span-full" style={{ backgroundColor: '#F0F0F1' }}>
          <div className="mx-auto w-full max-w-5xl px-4">
            {step === 'search' && (
              <div className="flex flex-col items-center" style={{ gap: '24px', padding: '56px 24px 240px 24px' }}>
                {/* state-layer */}
                <div
                  className="flex w-full flex-col items-stretch"
                  style={{
                    borderRadius: '28px',
                    gap: '56px',
                    paddingTop: '56px',
                  }}
                >
                  {/* Content + Message */}
                  <div className="flex w-full flex-col items-stretch" style={{ gap: '24px' }}>
                    {/* Header */}
                    <div className="flex w-full flex-col items-center" style={{ gap: '2px' }}>
                      <h2
                        style={{
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 700,
                          fontSize: '45px',
                          fontStyle: 'normal',
                          lineHeight: '52px',
                          letterSpacing: '-0.25px',
                          textAlign: 'center',
                          color: '#0B0B0C',
                          alignSelf: 'stretch',
                        }}
                      >
                        Hvaða tæki ertu með?
                      </h2>
                    </div>

                    {/* Input area */}
                    <div className="flex w-full flex-col items-stretch" style={{ gap: '16px' }}>
                      <div className="flex w-full flex-col items-stretch" style={{ gap: '8px' }}>
                        {/* Input wrapper */}
                        <div className="relative w-full" style={{ height: '56px' }}>
                          <div
                            className="absolute inset-0 flex items-center"
                            style={{
                              borderRadius: '100px',
                              border: '1px solid #A8A9AE',
                              backgroundColor: '#FCFCFC',
                              padding: '4px 12px',
                              alignItems: 'center',
                              flexShrink: 0,
                              alignSelf: 'stretch',
                            }}
                          >
                            {/* Search icon - 40x40 */}
                            <div
                              className="flex flex-shrink-0 items-center justify-center"
                              style={{ width: '40px', height: '40px' }}
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                                  fill="#181818"
                                />
                              </svg>
                            </div>

                            {/* Text input */}
                            <input
                              id="device-search"
                              type="text"
                              placeholder="Leitaðu að tæki"
                              value={searchTerm}
                              onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setError(null);
                              }}
                              autoComplete="off"
                              className="flex-1 border-none bg-transparent outline-none"
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '1.75em',
                                color: '#1E1E1C',
                                marginLeft: '8px',
                              }}
                            />

                            {/* Loading spinner */}
                            {loading && searchTerm && (
                              <div
                                className="flex flex-shrink-0 items-center justify-center"
                                style={{ width: '40px', height: '40px' }}
                              >
                                <svg
                                  className="h-6 w-6 animate-spin text-[#A8A9AE]"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                              </div>
                            )}

                            {/* Clear button */}
                            {!loading && searchTerm && (
                              <button
                                onClick={() => {
                                  setSearchTerm('');
                                  setDevices([]);
                                  setShowDropdown(false);
                                }}
                                className="hover:bg-gray-100 flex flex-shrink-0 items-center justify-center rounded-full transition-colors"
                                style={{ width: '40px', height: '40px' }}
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                                    fill="#181818"
                                  />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error message */}
                {error && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '24px',
                    }}
                  >
                    <p
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '1.5em',
                        textAlign: 'center',
                        color: '#505057',
                        margin: 0,
                      }}
                    >
                      Við fundum ekkert sem passar við leitina þína.
                    </p>
                    <p
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '1.5em',
                        textAlign: 'center',
                        color: '#505057',
                        margin: 0,
                      }}
                    >
                      Prófaðu að breyta leitarorði
                    </p>
                  </div>
                )}

                {/* Search results dropdown */}
                {devices.length > 0 && (
                  <div
                    className="search-results-dropdown"
                    style={{
                      display: 'flex',
                      padding: '24px',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      alignSelf: 'stretch',
                      borderRadius: '28px',
                      backgroundColor: '#FCFCFC',
                      boxShadow:
                        '0px 3px 6px 0px rgba(30, 30, 28, 0.05), 0px 11px 11px 0px rgba(30, 30, 28, 0.04), 0px 24px 15px 0px rgba(30, 30, 28, 0.03)',
                      maxHeight: '480px',
                      overflowY: 'auto',
                      marginTop: '12px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        maxWidth: '551px',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '4px',
                      }}
                    >
                      {devices.map((device) => (
                        <button
                          key={device.id}
                          onClick={() => selectDevice(device)}
                          className="text-left transition-colors hover:bg-[#F4F4F4]"
                          style={{
                            display: 'flex',
                            padding: '8px 16px',
                            alignItems: 'center',
                            gap: '12px',
                            alignSelf: 'stretch',
                            borderRadius: '16px',
                            border: 'none',
                            backgroundColor: 'transparent',
                          }}
                        >
                          {/* Device image */}
                          {device.imageUrl ? (
                            <img
                              src={device.imageUrl}
                              alt={device.productName}
                              className="object-contain"
                              style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '12px',
                                flexShrink: 0,
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div
                              className="flex flex-shrink-0 items-center justify-center"
                              style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '12px',
                              }}
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M17 1.01L7 1C5.9 1 5 1.9 5 3V21C5 22.1 5.9 23 7 23H17C18.1 23 19 22.1 19 21V3C19 1.9 18.1 1.01 17 1.01ZM17 19H7V5H17V19Z"
                                  fill="#A8A9AE"
                                />
                              </svg>
                            </div>
                          )}

                          {/* Device name */}
                          <div className="min-w-0 flex-1">
                            <p
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '1.75em',
                                color: '#181818',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                              }}
                            >
                              {device.productName}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 'grading' && selectedDevice && gradingQuestions.length > 0 && (
              <div className="grading-container flex flex-col items-center" style={{ gap: '24px', padding: '24px' }}>
                {/* Device header */}
                <div
                  className="flex w-full flex-col items-stretch md:flex-row md:items-center md:justify-between"
                  style={{
                    padding: '16px 24px',
                    borderRadius: '28px',
                    backgroundColor: '#FFFFFF',
                    boxShadow:
                      '0px 3px 6px 0px rgba(30, 30, 28, 0.05), 0px 11px 11px 0px rgba(30, 30, 28, 0.04), 0px 24px 15px 0px rgba(30, 30, 28, 0.03)',
                    gap: '16px',
                  }}
                >
                  <div className="flex items-center" style={{ gap: '16px' }}>
                    {selectedDevice.imageUrl && (
                      <img
                        src={selectedDevice.imageUrl}
                        alt={selectedDevice.productName}
                        className="object-contain"
                        style={{ width: '60px', height: '60px', borderRadius: '12px' }}
                      />
                    )}
                    <h3
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '1.5em',
                        color: '#181818',
                      }}
                    >
                      {selectedDevice.productName}
                    </h3>
                  </div>
                  <button
                    onClick={resetCalculator}
                    className="flex w-full items-center justify-center transition-colors hover:opacity-70 md:w-auto"
                    style={{
                      gap: '8px',
                      padding: '8px 16px',
                      borderRadius: '1000px',
                      border: '1px solid #A8A9AE',
                      backgroundColor: 'transparent',
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z"
                        fill="#181818"
                      />
                    </svg>
                    <span
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '1.43em',
                        color: '#181818',
                      }}
                    >
                      Velja annað tæki
                    </span>
                  </button>
                </div>

                {/* state-layer */}
                <div
                  className="flex w-full flex-col items-stretch"
                  style={{
                    borderRadius: '28px',
                    paddingBottom: '56px',
                  }}
                >
                  {/* Progress bar */}
                  <div
                    className="progress-bar-container flex w-full flex-col items-stretch"
                    style={{ gap: '10px', padding: '24px 0' }}
                  >
                    <div className="progress-bar-wrapper">
                      <div className="progress-bar-inner flex items-center" style={{ gap: '0' }}>
                        {gradingQuestions.map((q, index) => {
                          const isCompleted = index < currentQuestionIndex;
                          const isActive = index === currentQuestionIndex;

                          return (
                            <React.Fragment key={index}>
                              {/* Step pill */}
                              <div
                                className="step-pill flex cursor-pointer items-center justify-center transition-all"
                                style={{
                                  borderRadius: '1000px',
                                  padding: '12px 20px',
                                  gap: '8px',
                                  backgroundColor: isCompleted ? '#D1D1D6' : isActive ? '#D3E3E5' : 'transparent',
                                  border: isCompleted || isActive ? 'none' : '1px solid #A8A9AE',
                                }}
                                onClick={() => {
                                  if (index < currentQuestionIndex) {
                                    setCurrentQuestionIndex(index);
                                  }
                                }}
                              >
                                {isCompleted && (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="flex-shrink-0"
                                  >
                                    <path
                                      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                                      fill="#5E5E5E"
                                    />
                                  </svg>
                                )}
                                <span
                                  style={{
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    lineHeight: '1.43em',
                                    textAlign: 'center',
                                    color: isCompleted ? '#5E5E5E' : isActive ? '#23383C' : '#181818',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {STEP_LABEL_TRANSLATIONS[q.label] || q.label || q.question}
                                </span>
                              </div>
                              {/* Connecting line */}
                              {index < gradingQuestions.length - 1 && (
                                <div
                                  className="step-connector"
                                  style={{
                                    width: '40px',
                                    height: '1px',
                                    backgroundColor: '#A8A9AE',
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Question card */}
                  <div
                    style={{
                      display: 'flex',
                      padding: '48px',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '32px',
                      alignSelf: 'stretch',
                      borderRadius: '28px',
                      backgroundColor: '#FFFFFF',
                      boxShadow:
                        '0px 3px 6px 0px rgba(30, 30, 28, 0.05), 0px 11px 11px 0px rgba(30, 30, 28, 0.04), 0px 24px 15px 0px rgba(30, 30, 28, 0.03)',
                    }}
                    className="question-card"
                  >
                    {(() => {
                      const question = gradingQuestions[currentQuestionIndex];
                      return (
                        <>
                          {/* Question section */}
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              gap: '8px',
                              alignSelf: 'stretch',
                            }}
                          >
                            {/* Question title */}
                            <h3
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 700,
                                fontSize: '28px',
                                lineHeight: '1.3em',
                                color: '#0B0B0C',
                                margin: 0,
                              }}
                            >
                              {QUESTION_TRANSLATIONS[question.question] || question.question}
                            </h3>
                            {/* Subtitle with dropdown indicator */}
                            <button
                              onClick={() => setShowRequirements(!showRequirements)}
                              onMouseEnter={() => setIsHoveringDropdown(true)}
                              onMouseLeave={() => setIsHoveringDropdown(false)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: isHoveringDropdown ? '#DCDCDC' : 'transparent',
                                border: 'none',
                                padding: '8px 16px',
                                cursor: 'pointer',
                                borderRadius: '100px',
                                marginLeft: '-16px',
                                transition: 'background-color 0.2s ease',
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: 'Poppins, sans-serif',
                                  fontWeight: 400,
                                  fontSize: '14px',
                                  lineHeight: '1.5em',
                                  color: '#505057',
                                }}
                              >
                                Það sem þarf að virka
                              </span>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{
                                  transform: showRequirements ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease',
                                }}
                              >
                                <path d="M7 10l5 5 5-5H7z" fill="#505057" />
                              </svg>
                            </button>

                            {/* Requirements dropdown content */}
                            {showRequirements && question.infoCardText && (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '16px',
                                  alignSelf: 'stretch',
                                  marginTop: '8px',
                                  fontFamily: 'Poppins, sans-serif',
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: `<style>
                                    .info-card-content p { font-weight: 500; font-size: 16px; line-height: 1.5em; color: #0B0B0C; margin: 0 0 8px 0; }
                                    .info-card-content ul { margin: 0 0 16px 0; padding-left: 20px; display: flex; flex-direction: column; gap: 8px; }
                                    .info-card-content li { font-weight: 400; font-size: 14px; line-height: 1.5em; color: #0B0B0C; }
                                    .info-card-content p strong span[style*="color"] { display: inline-block !important; padding: 16px; border-radius: 12px; background-color: #D3E3E5; font-weight: 400 !important; font-size: 14px; color: #23383C !important; }
                                    .info-card-content p:has(strong span[style*="color"]) { display: block; padding: 0; margin-top: 8px; }
                                  </style><div class="info-card-content">${translateInfoCardText(
                                    question.infoCardText
                                  )}</div>`,
                                }}
                              />
                            )}
                          </div>

                          {/* Answer options as horizontal pills */}
                          <div className="flex flex-col sm:flex-row" style={{ gap: '12px', width: '100%' }}>
                            {question.options.map((option) => {
                              const isSelected = answers[question.id] === option.id.toString();
                              const isHovered = hoveredOption === option.id;
                              const isActive = activeOption === option.id;

                              return (
                                <button
                                  key={option.id}
                                  className="w-full sm:w-auto"
                                  onClick={() => {
                                    const newAnswers = { ...answers, [question.id]: option.id.toString() };
                                    setAnswers(newAnswers);
                                    setError(null);

                                    // Auto-advance to next question or submit
                                    setTimeout(() => {
                                      if (currentQuestionIndex < gradingQuestions.length - 1) {
                                        setCurrentQuestionIndex(currentQuestionIndex + 1);
                                      } else {
                                        // Pass the updated answers directly to avoid state timing issues
                                        submitGrading(newAnswers);
                                      }
                                    }, 150);
                                  }}
                                  onMouseEnter={() => setHoveredOption(option.id)}
                                  onMouseLeave={() => setHoveredOption(null)}
                                  onMouseDown={() => setActiveOption(option.id)}
                                  onMouseUp={() => setActiveOption(null)}
                                  style={{
                                    display: 'flex',
                                    padding: '12px 24px',
                                    alignItems: 'center',
                                    gap: '8px',
                                    borderRadius: '1000px',
                                    border: isSelected
                                      ? '2px solid #23383C'
                                      : isActive && !isSelected
                                      ? '1px solid #FFE5E5'
                                      : isHovered
                                      ? '1px solid #FFE5E5'
                                      : '1px solid #A8A9AE',
                                    backgroundColor: isSelected
                                      ? '#D3E3E5'
                                      : isActive && !isSelected
                                      ? '#FFE5E5'
                                      : isHovered
                                      ? '#FFE5E5'
                                      : 'transparent',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <span
                                    style={{
                                      fontFamily: 'Poppins, sans-serif',
                                      fontWeight: 700,
                                      fontSize: '16px',
                                      lineHeight: '1.5em',
                                      color: isSelected
                                        ? '#0B0B0C'
                                        : isActive && !isSelected
                                        ? '#E63946'
                                        : isHovered
                                        ? '#E63946'
                                        : '#181818',
                                      whiteSpace: 'nowrap',
                                      transition: 'color 0.2s ease',
                                    }}
                                  >
                                    {ANSWER_TRANSLATIONS[option.name] || option.name}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {step === 'result' && priceQuote && selectedDevice && (
              <div className="result-container flex flex-col items-stretch" style={{ padding: '24px 24px 56px' }}>
                {/* Product description card */}
                <div
                  className="result-card flex w-full flex-col overflow-hidden md:flex-row md:items-stretch"
                  style={{
                    borderRadius: '28px',
                    backgroundColor: '#FFFFFF',
                    boxShadow:
                      '0px 3px 6px 0px rgba(30, 30, 28, 0.05), 0px 11px 11px 0px rgba(30, 30, 28, 0.04), 0px 24px 15px 0px rgba(30, 30, 28, 0.03)',
                  }}
                >
                  {/* Device image - top on mobile, left side on desktop */}
                  <div
                    className="result-image flex w-full flex-shrink-0 items-center justify-center md:w-[280px]"
                    style={{
                      backgroundColor: '#F4F4F4',
                      padding: '32px',
                    }}
                  >
                    {selectedDevice.imageUrl ? (
                      <img
                        src={selectedDevice.imageUrl}
                        alt={selectedDevice.productName}
                        className="object-contain"
                        style={{ maxWidth: '200px', maxHeight: '200px' }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M17 1.01L7 1C5.9 1 5 1.9 5 3V21C5 22.1 5.9 23 7 23H17C18.1 23 19 22.1 19 21V3C19 1.9 18.1 1.01 17 1.01ZM17 19H7V5H17V19Z"
                          fill="#A8A9AE"
                        />
                      </svg>
                    )}
                  </div>

                  {/* Content - bottom on mobile, right side on desktop */}
                  <div
                    className="result-content flex flex-1 flex-col justify-center"
                    style={{ padding: '40px 48px', gap: '16px' }}
                  >
                    {/* Device name */}
                    <h2
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 700,
                        fontSize: '24px',
                        lineHeight: '1.3em',
                        color: '#181818',
                        margin: 0,
                      }}
                    >
                      {selectedDevice.productName}
                    </h2>

                    {/* Subtitle */}
                    <p
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '1.6em',
                        color: '#505057',
                        margin: 0,
                      }}
                    >
                      Miðað við þessar upplýsingar er verðmatið á þínu tæki:
                    </p>

                    {/* Price - Icelandic formatting with dots */}
                    <p
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 700,
                        fontSize: '28px',
                        lineHeight: '1.3em',
                        color: '#181818',
                        margin: 0,
                      }}
                    >
                      {priceQuote.price.toLocaleString('de-DE')} kr.
                    </p>

                    {/* Description text */}
                    <p
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '1.6em',
                        color: '#505057',
                        margin: 0,
                        maxWidth: '400px',
                      }}
                    >
                      Kiktu til okkar í næstu verslun til að endurgræða, þar staðfestum við endanlegt verðmat.
                    </p>

                    {/* Action Buttons */}
                    <div className="result-buttons flex flex-col md:flex-row" style={{ gap: '12px', marginTop: '8px' }}>
                      {/* Meta annað tæki */}
                      <button
                        onClick={resetCalculator}
                        className="flex w-full items-center justify-center transition-colors hover:bg-[#F4F4F4] md:w-auto"
                        style={{
                          borderRadius: '1000px',
                          border: '1px solid #A8A9AE',
                          height: '48px',
                          padding: '10px 24px',
                          gap: '8px',
                        }}
                      >
                        {/* Swap icon */}
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z"
                            fill="#181818"
                          />
                        </svg>
                        <span
                          style={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 700,
                            fontSize: '14px',
                            lineHeight: '1.25em',
                            color: '#181818',
                          }}
                        >
                          Meta annað tæki
                        </span>
                      </button>

                      {/* Skoða notað og nýtt */}
                      <a
                        href="https://www.syn.is/notad-upp-i-nytt"
                        className="flex w-full items-center justify-center transition-colors hover:bg-[#F4F4F4] md:w-auto"
                        style={{
                          borderRadius: '1000px',
                          border: '1px solid #A8A9AE',
                          height: '48px',
                          padding: '10px 24px',
                          textDecoration: 'none',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 700,
                            fontSize: '14px',
                            lineHeight: '1.25em',
                            color: '#181818',
                          }}
                        >
                          Skoða notað og nýtt
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  try {
    const apiUrl = contentfulAPIURL(
      process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
      process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
    );
    const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

    const footer = await queryFooter(apolloClient);
    const navbar = await queryNavbar(apolloClient);

    return {
      props: {
        navbar: navbar.data,
        footer: footer.data,
        radioSchedules: await getRadioSchedules(),
      },
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);

    return {
      props: {
        navbar: {} as NavbarQuery,
        footer: {} as FooterQuery,
        radioSchedules: {},
      },
    };
  }
};

export default ReiknivelPage;
